import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Box } from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useDeleteZone } from "../zones-setup-page/hooks/useDeleteZone";

type DeleteZoneProps = {
  zoneDeviceId: string;
  zoneId: string;
  serviceId: string;
  zoneName: string;
  nodeId: string;
  noRedirection?: boolean;
  children: ReactNode;
};

const DeleteZoneContainer = ({
  zoneId,
  zoneName,
  zoneDeviceId,
  serviceId,
  noRedirection,
  nodeId,
  children,
}: DeleteZoneProps): JSX.Element => {
  const { deviceId = "" } = useParams<{ deviceId: string }>();

  const { syncShadowsForNode } = useSyncShadow();

  const { deleteZone, loading } = useDeleteZone();

  const confirm = useConfirm();
  const navigate = useNavigate();

  const handleDeviceDetailsPageClick = (): void => {
    confirm({
      title: `Are you sure you want to delete the ${zoneName} zone?`,
      description: "All data will be deleted and can't be restored",
      confirmationText: "Delete",
      cancellationText: "Cancel",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        disabled: loading,
      },
    })
      .then(async (): Promise<void> => {
        await deleteZone({
          deviceId: zoneDeviceId,
          zoneId,
          serviceId,
          name: zoneName,
        });

        if (noRedirection) return;

        navigate(`/device/${encodeURIComponent(deviceId)}`);
      })
      .finally((): void => {
        syncShadowsForNode(nodeId);
      });
  };

  return (
    <Box
      sx={{ padding: "0", display: "flex" }}
      onClick={handleDeviceDetailsPageClick}
    >
      {children}
    </Box>
  );
};

export default DeleteZoneContainer;
